import type {
    default as PopupMenu,
    PopupMenuProvider,
    PopupMenuTarget,
} from 'diagram-js/lib/features/popup-menu/PopupMenu';
import type { PopupMenuEntriesProvider } from 'diagram-js/lib/features/popup-menu/PopupMenuProvider';

export const PopupMenuProviderType = {
    Append: 'append',
    Create: 'create',
    Replace: 'replace',
} as const;

export type PopupMenuProviderType =
    (typeof PopupMenuProviderType)[keyof typeof PopupMenuProviderType];

export abstract class AbstractPopupMenuProvider implements PopupMenuProvider {
    static readonly $inject: string[] = ['popupMenu'];

    protected abstract getPopupMenuType(): PopupMenuProviderType;

    private readonly entryKeysToDelete: string[];

    constructor(popupMenu: PopupMenu) {
        this.entryKeysToDelete = this.getEntryKeysToDelete();
        popupMenu.registerProvider('bpmn-' + this.getPopupMenuType(), this);
    }

    protected mapPopupMenuTypeToEntryPrefix(): string {
        const type = this.getPopupMenuType();
        if (type === 'replace') {
            return type + '-with-';
        }
        return type + '-';
    }

    protected getEntryKeysToDelete(): string[] {
        const prefix = this.mapPopupMenuTypeToEntryPrefix();
        return [
            'ad-hoc-subprocess',
            'call-activity',
            'collapsed-ad-hoc-subprocess',
            'collapsed-pool',
            'compensation-end',
            'compensation-intermediate-throw',
            'complex-gateway',
            'conditional-intermediate-catch',
            'escalation-end',
            'escalation-intermediate-throw',
            'expanded-ad-hoc-subprocess',
            'expanded-pool',
            'data-store-reference',
            'data-object-reference',
            'inclusive-gateway',
            'link-intermediate-catch',
            'link-intermediate-throw',
            'manual-task',
            'message-intermediate-throw',
            'none-intermediate-throwing',
            'parallel-gateway',
            'receive-task',
            'replace-with-non-interrupting-signal-start',
            'rule-task',
            'send-task',
            'signal-end',
            'signal-intermediate-throw',
            'signal-start',
            'expanded-subprocess',
            'transaction',
            'event-based-gateway',
            'conditional-start',
        ].map((entry) => prefix + entry);
    }

    private readonly getEntries: PopupMenuEntriesProvider = (entries) => {
        this.entryKeysToDelete.forEach((key) => {
            delete entries[key];
        });
        return entries;
    };

    getPopupMenuEntries(_target: PopupMenuTarget): PopupMenuEntriesProvider {
        return this.getEntries;
    }
}
